import React, { useCallback, useEffect, useState } from 'react';
import { getSessionMetadata, listProjectPromptButtons, mergeSessionMetadata } from '../utils/api';
import {
  buildWorkflowPromptWarnings,
  pickWorkflowSendContent,
  workflowNeedsContentMessage,
} from '../utils/projectWorkflowPrompts';

/**
 * Per-project workflow catalog buttons for chat composers.
 * Collapsed: one full-width row of buttons. Expanded: wraps to multiple rows.
 * Expand preference is stored on the chat session (metadata.workflow_buttons_expanded).
 */
export default function ProjectWorkflowButtonsCarousel({
  projectId,
  sessionId = null,
  disabled = false,
  composerText = '',
  messages = [],
  onSetComposerText,
  onRunWorkflow,
  projectToolIds = new Set(),
  projectGroupSlugs = new Set(),
}) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setPrompts([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await listProjectPromptButtons(projectId);
        if (!cancelled) {
          setPrompts(Array.isArray(list) ? list : []);
        }
      } catch (e) {
        console.error('[ProjectWorkflowButtons] load failed:', e);
        if (!cancelled) setPrompts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (!sessionId) {
      setExpanded(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getSessionMetadata(sessionId);
        if (!cancelled) {
          setExpanded(!!data?.metadata?.workflow_buttons_expanded);
        }
      } catch (_) {
        if (!cancelled) setExpanded(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const persistExpanded = useCallback(async (next) => {
    setExpanded(next);
    if (!sessionId) return;
    try {
      await mergeSessionMetadata(sessionId, { workflow_buttons_expanded: next });
    } catch (e) {
      console.warn('[ProjectWorkflowButtons] failed to persist expand state:', e?.message || e);
    }
  }, [sessionId]);

  const handleExecute = useCallback(async (prompt) => {
    if (!prompt?.id) return;
    const content = pickWorkflowSendContent({
      promptTemplate: prompt.prompt,
      composerText,
      messages,
    });
    if (!content) {
      onSetComposerText?.(workflowNeedsContentMessage(prompt));
      return;
    }
    const warnings = buildWorkflowPromptWarnings(prompt, {
      availableToolIds: projectToolIds,
      groupSlugs: projectGroupSlugs,
    });
    if (warnings.length > 0) {
      const proceed = window.confirm(`${warnings.join('\n\n')}\n\nRun this workflow anyway?`);
      if (!proceed) return;
    }
    await onRunWorkflow?.({ content, workflowPromptId: prompt.id, prompt });
  }, [composerText, messages, onSetComposerText, onRunWorkflow, projectToolIds, projectGroupSlugs]);

  if (!loading && prompts.length === 0) {
    return null;
  }

  const showExpandToggle = prompts.length > 1;

  return (
    <div
      className={`project-prompts-bar${expanded ? ' is-expanded' : ' is-collapsed'}`}
      role="toolbar"
      aria-label="Project workflow prompts"
    >
      <div className="project-prompts-bar__buttons">
        {loading && prompts.length === 0 ? (
          <button type="button" className="mi-btn prompt-btn" disabled title="Loading workflows">
            <i className="fas fa-spinner fa-pulse" /> Workflows…
          </button>
        ) : (
          prompts.map((prompt) => (
            <button
              key={prompt.id || prompt.slug || prompt.name}
              type="button"
              className="mi-btn prompt-btn"
              title={prompt?.description || 'Execute workflow'}
              onClick={() => handleExecute(prompt)}
              disabled={disabled}
            >
              <i className="fas fa-magic" />
              <span className="prompt-btn__label">{prompt?.name || 'Workflow'}</span>
            </button>
          ))
        )}
      </div>
      {showExpandToggle && (
        <button
          type="button"
          className="mi-btn project-prompts-bar__expand"
          title={expanded ? 'Collapse workflow buttons to one row' : 'Expand workflow buttons to multiple rows'}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse workflow buttons' : 'Expand workflow buttons'}
          onClick={() => persistExpanded(!expanded)}
        >
          <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} />
        </button>
      )}
    </div>
  );
}
